<template>
  <div class="card-body"> 
    <div class="row row-spacing">
      <div class="col-md-12">
        <div class="row align-items-end no-gutters">
          <div class="col-md-8">
            <label>Search Domains</label>
            <div class="input-group">
              <input
                type="search"
                class="form-control"
                placeholder="Search by domain"
                v-model="searchTerm"
              />
              <div class="input-group-append">
                <span class="input-group-text"><i class="fa fa-search"></i></span>
              </div>
            </div>
          </div>
          <div class="col-md-4 text-right">
            <button class="btn btn-primary" @click="openCreateModal()">
              Add Domain
            </button>
          </div>
        </div>
        <hr>

        <div class="table-responsive">
          <table class="table table-bordered table-hover">
            <thead>
              <tr>
                <th>Domain</th>
                <th style="width: 8rem"></th>
              </tr>
            </thead>
            <tbody v-if="filteredDomains.length">
              <tr v-for="domain in filteredDomains" :key="domain.supplierDomainNumber">
                <td>{{ domain.name }}</td>
                <td class="text-center">
                  <button
                    class="btn btn-sm btn-info m-1"
                    type="button"
                    v-tooltip="'Edit Domain'"
                    @click="openEditModal(domain)"
                  >
                    <em class="fa fa-edit fa-fw"></em>
                  </button>
                  <button
                    class="btn btn-sm btn-primary"
                    type="button"
                    v-tooltip="'Delete Domain'"
                    v-allowed:view="['STAFF']"
                    @click="deleteDomain(domain.supplierDomainNumber)"
                  >
                    <em class="fa fa-trash fa-fw"></em>
                  </button>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="2">No Domains available</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <modal
        :title="modalTitle"
        ref="domainModal"
        size="modal-lg"
        :disable-save="modalSaving"
        @save="saveDomain()"
        @dismiss="resetModalState()"
        @close="resetModalState()"
      >
        <div class="row">
          <div class="form-group col-md-6">
            <label>Domain</label>
            <input
              v-model="modalDomain.name"
              type="text"
              class="form-control"
              :class="{ 'has-error': validationErrors.name }"
            />
            <small class="has-error mt-1" v-if="validationErrors.name">
              {{ validationErrors.name }}
            </small>
          </div>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import Modal from "@/components/GeneralComponents/Modal";

export default {
  components: {
    Modal,
  },
  data: function() {
    return {
      supplierNumber: 0,
      supplier: {},
      domains: [],
      searchTerm: "",
      emptyDomain: {},
      modalDomain: {},
      validationErrors: {},
      modalSaving: false,
    };
  },
  created() {
    this.$root.$emit("SET_PAGE_TITLE", this.$route.meta.title);
  },
  mounted: function() {
    this.supplierNumber = parseInt(this.$route.params.supplierNumber);
    this.loadPage();
  },
  computed: {
    modalTitle() {
      return this.modalDomain.supplierDomainNumber
        ? "Edit Supplier Domain"
        : "Add Supplier Domain";
    },
    filteredDomains() {
      if (!this.searchTerm || this.searchTerm.trim() === "") {
        return this.domains;
      }

      const searchValue = this.searchTerm.trim().toLowerCase();

      return this.domains.filter((domain) => {
        return domain.name && domain.name.toLowerCase().includes(searchValue);
      });
    },
  },
  methods: {
    loadPage() {
      return Promise.all([
        this.getSupplier(),
        this.getDomains(),
        this.getEmptyDomain(),
      ]);
    },
    getSupplier() {
      return this.$http
        .get(this.$config.aimsAPIv3 + "suppliers/" + this.supplierNumber)
        .then(
          (response) => {
            this.supplier = response.data;
            this.$root.$emit(
              "SET_PAGE_TITLE",
              this.$route.meta.title + " - " + this.supplier.name
            );
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Supplier", error);
          }
        );
    },
    getDomains() {
      return this.$http
        .get(this.$config.aimsAPIv3 + "suppliers/" + this.supplierNumber + "/domains")
        .then(
          (response) => {
            this.domains = response.data;
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Domains", error);
          }
        );
    },
    getEmptyDomain() {
      return this.$http.get(this.$config.aimsAPIv3 + "suppliers/empty/domain").then(
        (response) => {
          this.emptyDomain = response.data;
        },
        (error) => {
          console.error(error);
          this.showError("Error Fetching Empty Domain", error);
        }
      );
    },
    buildEmptyDomain() {
      return {
        class:
          this.emptyDomain.class ||
          "za.co.adept.aims.product.supplier.SupplierDomain",
        supplierDomainNumber: this.emptyDomain.supplierDomainNumber || 0,
        supplierNumber: this.supplierNumber || this.emptyDomain.supplierNumber || 0,
        name: this.emptyDomain.name || "",
      };
    },
    openCreateModal() {
      this.validationErrors = {};
      this.modalDomain = this.buildEmptyDomain();
      this.$refs.domainModal.open();
    },
    openEditModal(domain) {
      this.validationErrors = {};
      this.modalDomain = Object.assign(this.buildEmptyDomain(), domain);
      this.$refs.domainModal.open();
    },
    resetModalState() {
      this.modalDomain = {};
      this.validationErrors = {};
      this.modalSaving = false;
    },
    validateDomainName(name) {
      if (!name || name.trim() === "") {
        return "Please capture a domain.";
      }

      const normalizedName = name.trim().toLowerCase();
      const domainRegex =
        /^(?=.{4,128}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

      if (!domainRegex.test(normalizedName)) {
        return "Please capture a valid domain, for example supplier.co.za.";
      }

      return "";
    },
    validateDomain(domain) {
      const errors = {};

      const nameError = this.validateDomainName(domain.name);
      if (nameError) {
        errors.name = nameError;
      }

      this.validationErrors = errors;
      return Object.keys(errors).length === 0;
    },
    saveDomain() {
      if (!this.validateDomain(this.modalDomain)) {
        const firstValidationError = Object.values(this.validationErrors)[0];
        this.showWarningAlert(firstValidationError);
        return;
      }

      this.modalSaving = true;

      const domain = Object.assign({}, this.modalDomain, {
        supplierNumber: this.supplierNumber,
        name: this.modalDomain.name.trim().toLowerCase(),
      });

      if (domain.supplierDomainNumber > 0) {
        this.updateDomain(domain);
        return;
      }

      this.createDomain(domain);
    },
    createDomain(domain) {
      this.$http
        .post(
          this.$config.aimsAPIv3 +
            "suppliers/" +
            this.supplierNumber +
            "/create/domain",
          domain
        )
        .then(
          () => {
            this.finishSave("Supplier domain saved");
          },
          (error) => {
            this.modalSaving = false;
            console.error(error);
            this.showError("Error Creating Domain", error);
          }
        );
    },
    updateDomain(domain) {
      this.$http.put(this.$config.aimsAPIv3 + "suppliers/update/domain", domain).then(
        () => {
          this.finishSave("Supplier domain updated");
        },
        (error) => {
          this.modalSaving = false;
          console.error(error);
          this.showError("Error Updating Domain", error);
        }
      );
    },
    finishSave(successMessage) {
      this.modalSaving = false;
      this.$refs.domainModal.close();
      this.showSuccess(successMessage);
      this.resetModalState();
      this.loadPage();
    },
    deleteDomain(domainNumber) {
      this.$http.delete(this.$config.aimsAPIv3 + "suppliers/delete/domain/" + domainNumber).then(
        () => {
          this.showSuccess("Supplier domain deleted");
          this.getDomains();
        },
        (error) => {
          console.error(error);
          this.showError("Error Deleting Domain", error);
        }
      );
    },
  },
};
</script>
